/**
 * QuickshopProductForm — keeps the quick shop modal's hidden variant id, price, and add-to-cart
 * button in sync with the colour/size radios in `quickshop-options.liquid`.
 *
 * Matches selected options against the variants JSON rendered in `quickshop-purchase.liquid`
 * (no fetch, no morph — see that snippet's doc for why) and writes the result into:
 *   [data-quickshop-variant-id]        — the form's hidden variant id input
 *   [data-quickshop-add-to-cart]       — disabled state, via its own data-add-text /
 *                                        data-sold-out-text / data-unavailable-text
 *   [data-quickshop-add-to-cart-label] — the button's visible label
 *   [data-quickshop-price-current]     — the current price
 *   [data-quickshop-price-compare]     — the struck-through compare-at price, hidden when absent
 *
 * The actual `/cart/add` submit is Horizon's `<product-form-component>`, untouched here.
 *
 * `refresh()` re-reads the variants script and re-syncs state without re-adding the `change`
 * listener. `quickshop-bundle-picker.js` calls it after morphing a sibling bundle's markup in:
 * that morph replaces this element's children (including the variants script) but, since the
 * element itself is never replaced, `connectedCallback` never runs again on its own.
 */

class QuickshopProductForm extends HTMLElement {
  /** @type {Array<{id: number, available: boolean, options: string[], price: string, compare_at_price: string | null}>} */
  #variants = [];

  connectedCallback() {
    this.addEventListener("change", this.#handleChange);
    this.refresh();
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.#handleChange);
  }

  refresh() {
    const script = this.querySelector("[data-quickshop-variants]");
    this.#variants = [];
    if (script?.textContent) {
      try {
        this.#variants = JSON.parse(script.textContent);
      } catch {
        this.#variants = [];
      }
    }

    this.#syncSelectedVariant();
  }

  get #fieldsets() {
    return Array.from(this.querySelectorAll("[data-quickshop-fieldset]"));
  }

  get #variantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector("[data-quickshop-variant-id]"));
  }

  get #addToCartButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector("[data-quickshop-add-to-cart]"));
  }

  get #addToCartLabel() {
    return this.querySelector("[data-quickshop-add-to-cart-label]");
  }

  get #priceCurrent() {
    return this.querySelector("[data-quickshop-price-current]");
  }

  get #priceCompare() {
    return /** @type {HTMLElement | null} */ (this.querySelector("[data-quickshop-price-compare]"));
  }

  #selectedOptionValues() {
    return this.#fieldsets.map((fieldset) => {
      const checked = /** @type {HTMLInputElement | null} */ (fieldset.querySelector("input:checked"));
      return checked?.value ?? null;
    });
  }

  #syncSelectedLabels() {
    for (const fieldset of this.#fieldsets) {
      const checked = /** @type {HTMLInputElement | null} */ (fieldset.querySelector("input:checked"));
      const label = fieldset.querySelector("[data-quickshop-selected-value]");
      if (label) label.textContent = checked?.value ?? "";

      for (const option of fieldset.querySelectorAll(".quickshop-options__label")) {
        const input = /** @type {HTMLInputElement | null} */ (option.querySelector("input"));
        if (input?.checked) {
          option.setAttribute("data-current-checked", "true");
        } else {
          option.removeAttribute("data-current-checked");
        }
      }
    }
  }

  #findMatchingVariant() {
    const selected = this.#selectedOptionValues();
    if (selected.length === 0) return this.#variants[0];
    if (selected.some((value) => value === null)) return undefined;
    return this.#variants.find((variant) => variant.options.every((value, index) => value === selected[index]));
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== "radio") return;
    this.#syncSelectedVariant();
  };

  #syncSelectedVariant() {
    this.#syncSelectedLabels();

    const variant = this.#findMatchingVariant();

    const input = this.#variantIdInput;
    if (input) input.value = variant ? String(variant.id) : "";

    const button = this.#addToCartButton;
    const label = this.#addToCartLabel;
    if (button) {
      const available = Boolean(variant?.available);
      button.disabled = !available;
      if (label) {
        label.textContent = !variant
          ? (button.dataset.unavailableText ?? "")
          : available
            ? (button.dataset.addText ?? "")
            : (button.dataset.soldOutText ?? "");
      }
    }

    if (variant) this.#syncPrice(variant);
  }

  /** @param {{price: string, compare_at_price: string | null}} variant */
  #syncPrice(variant) {
    const current = this.#priceCurrent;
    const compare = this.#priceCompare;

    if (current) current.textContent = variant.price;

    if (compare) {
      compare.textContent = variant.compare_at_price ?? "";
      compare.hidden = !variant.compare_at_price;
    }
  }
}

if (!customElements.get("quickshop-product-form")) {
  customElements.define("quickshop-product-form", QuickshopProductForm);
}

export default QuickshopProductForm;
